import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { STATUS_SNACKBAR_AUTO_HIDE_MS } from '../constants/offlineStatusToolbarLabels';
import { useOfflinePunchQueue } from './useOfflinePunchQueue';

export function useOfflineStatusToolbar() {
  const {
    isOffline,
    isStatusUiEnabled,
    pendingCount,
    hasFailedPunch,
    offlineSnackbarRequested,
    onlineSnackbarRequested,
    openDrawer,
    registerClosePersistentOfflineSnackbar,
    acknowledgeOfflineSnackbar,
    acknowledgeOnlineSnackbar,
  } = useOfflinePunchQueue();

  const [isOfflineSnackbarManualOpen, setIsOfflineSnackbarManualOpen] =
    useState(false);
  const [isOfflineSnackbarLatched, setIsOfflineSnackbarLatched] =
    useState(false);
  const [isOnlineSnackbarOpen, setIsOnlineSnackbarOpen] = useState(false);

  const prevOfflineSnackbarRequestedRef = useRef(false);
  const prevOnlineSnackbarRequestedRef = useRef(false);
  const prevIsOfflineRef = useRef(isOffline);

  useLayoutEffect(() => {
    const wasOffline = prevIsOfflineRef.current;

    const offlineRising =
      offlineSnackbarRequested && !prevOfflineSnackbarRequestedRef.current;
    if (offlineRising) {
      setIsOfflineSnackbarLatched(true);
      acknowledgeOfflineSnackbar();
    }

    if (wasOffline && !isOffline) {
      setIsOfflineSnackbarLatched(false);
    }

    if (isOffline && isOnlineSnackbarOpen) {
      setIsOnlineSnackbarOpen(false);
    }

    const onlineRising =
      onlineSnackbarRequested && !prevOnlineSnackbarRequestedRef.current;
    if (onlineRising) {
      setIsOnlineSnackbarOpen(true);
      setIsOfflineSnackbarLatched(false);
      setIsOfflineSnackbarManualOpen(false);
      acknowledgeOnlineSnackbar();
    }

    prevIsOfflineRef.current = isOffline;
    prevOfflineSnackbarRequestedRef.current = offlineSnackbarRequested;
    prevOnlineSnackbarRequestedRef.current = onlineSnackbarRequested;
  }, [
    isOffline,
    offlineSnackbarRequested,
    onlineSnackbarRequested,
    isOnlineSnackbarOpen,
    acknowledgeOfflineSnackbar,
    acknowledgeOnlineSnackbar,
  ]);

  const showWifiToolbar = isStatusUiEnabled && isOffline;
  const showQueueToolbar = isStatusUiEnabled && (isOffline || pendingCount > 0);
  const visible = showWifiToolbar || showQueueToolbar;

  const isOfflineSnackbarOpen =
    isOffline && (isOfflineSnackbarManualOpen || isOfflineSnackbarLatched);

  const closeOfflineSnackbar = useCallback(() => {
    setIsOfflineSnackbarLatched(false);
    setIsOfflineSnackbarManualOpen(false);
  }, []);

  const closeOnlineSnackbar = useCallback(() => {
    setIsOnlineSnackbarOpen(false);
    acknowledgeOnlineSnackbar();
  }, [acknowledgeOnlineSnackbar]);

  const toggleOfflineSnackbar = useCallback(() => {
    if (!showWifiToolbar) {
      return;
    }

    setIsOfflineSnackbarManualOpen((previous) => !previous);
  }, [showWifiToolbar]);

  const handleOfflineSnackbarClose = useCallback(() => {
    if (isOfflineSnackbarManualOpen) {
      return;
    }

    acknowledgeOfflineSnackbar();
    closeOfflineSnackbar();
  }, [
    acknowledgeOfflineSnackbar,
    closeOfflineSnackbar,
    isOfflineSnackbarManualOpen,
  ]);

  const openOfflinePunchDrawer = useCallback(() => {
    openDrawer();
  }, [openDrawer]);

  const offlineSnackbarAutoHideDuration = isOfflineSnackbarManualOpen
    ? null
    : STATUS_SNACKBAR_AUTO_HIDE_MS;

  useEffect(() => {
    registerClosePersistentOfflineSnackbar(closeOfflineSnackbar);
  }, [registerClosePersistentOfflineSnackbar, closeOfflineSnackbar]);

  return {
    visible,
    showWifiToolbar,
    showQueueToolbar,
    isOfflineSnackbarOpen,
    isOnlineSnackbarOpen,
    pendingCount,
    hasFailedPunch,
    toggleOfflineSnackbar,
    openOfflinePunchDrawer,
    closeOfflineSnackbar,
    closeOnlineSnackbar,
    handleOfflineSnackbarClose,
    offlineSnackbarAutoHideDuration,
  };
}
